import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type OfflineCacheState = {
  state: "idle" | "prefetching" | "success" | "error";
  total: number;
  completed: number;
  currentKey: string | null;
  error: string | null;
};

type OfflineCacheDispatch = {
  start: (total: number) => void;
  progress: (completed: number, key?: string | null) => void;
  complete: () => void;
  error: (message?: string) => void;
  reset: () => void;
};

const defaultState: OfflineCacheState = {
  state: "idle",
  total: 0,
  completed: 0,
  currentKey: null,
  error: null,
};

const OfflineCacheStatusContext = createContext<OfflineCacheState>(defaultState);
const OfflineCacheDispatchContext = createContext<OfflineCacheDispatch | undefined>(undefined);

export const OfflineCacheStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<OfflineCacheState>(defaultState);

  const start = useCallback((total: number) => {
    if (total <= 0) {
      setStatus(defaultState);
      return;
    }
    setStatus({
      state: "prefetching",
      total,
      completed: 0,
      currentKey: null,
      error: null,
    });
  }, []);

  const progress = useCallback((completed: number, key?: string | null) => {
    setStatus((previous) => {
      if (previous.state !== "prefetching") {
        return previous;
      }
      const nextCompleted = Math.min(completed, previous.total);
      return {
        ...previous,
        completed: nextCompleted,
        currentKey: key === undefined ? previous.currentKey : key,
      };
    });
  }, []);

  const complete = useCallback(() => {
    setStatus((previous) => ({
      ...previous,
      state: "success",
      completed: previous.total,
      currentKey: null,
      error: null,
    }));
  }, []);

  const reportError = useCallback((message?: string) => {
    setStatus({
      state: "error",
      total: 0,
      completed: 0,
      currentKey: null,
      error: message ?? "Offline caching failed. Please try again.",
    });
  }, []);

  const reset = useCallback(() => {
    setStatus(defaultState);
  }, []);

  useEffect(() => {
    if (status.state === "success") {
      const timer = setTimeout(() => {
        setStatus(defaultState);
      }, 2000);
      return () => clearTimeout(timer);
    }
    if (status.state === "error") {
      const timer = setTimeout(() => {
        setStatus(defaultState);
      }, 4000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [status.state]);

  const dispatchValue = useMemo<OfflineCacheDispatch>(
    () => ({
      start,
      progress,
      complete,
      error: reportError,
      reset,
    }),
    [start, progress, complete, reportError, reset],
  );

  return (
    <OfflineCacheStatusContext.Provider value={status}>
      <OfflineCacheDispatchContext.Provider value={dispatchValue}>
        {children}
      </OfflineCacheDispatchContext.Provider>
    </OfflineCacheStatusContext.Provider>
  );
};

export const useOfflineCacheStatus = (): OfflineCacheState => useContext(OfflineCacheStatusContext);

export const useOfflineCacheDispatch = (): OfflineCacheDispatch => {
  const context = useContext(OfflineCacheDispatchContext);
  if (!context) {
    throw new Error("useOfflineCacheDispatch must be used within an OfflineCacheStatusProvider");
  }
  return context;
};
